// src/services/quiz/quizStorageService.ts
// UPDATED: Fixed removeQuizState and loadQuizState to work across browser sessions/tabs

export interface QuizState {
  moduleName: string;
  week: number;
  currentQuestionIndex: number;
  userAnswers: Map<string, any>;
  startTime: number;
  lastUpdate: number;
  quizId: string;
  totalQuestions: number;
  progressStats: {
    completed: number;
    skipped: number;
    required: number;
    requiredCompleted: number;
  };
}

interface SerializableQuizState extends Omit<QuizState, "userAnswers"> {
  userAnswers: Array<[string, any]>;
  metadata: StorageMetadata;
}

interface StorageMetadata {
  version: string;
  timestamp: number;
  browser: string;
  tabId?: string;
}

export interface SaveResult {
  success: boolean;
  error?:
    | "QUOTA_EXCEEDED"
    | "STORAGE_UNAVAILABLE"
    | "SERIALIZATION_ERROR"
    | "UNKNOWN_ERROR";
  message?: string;
  cleanupPerformed?: boolean;
}

class QuizStorageService {
  private static instance: QuizStorageService;
  private readonly STORAGE_KEY_PREFIX = "eap_quiz_state_";
  private readonly METADATA_KEY = "eap_quiz_metadata";
  private readonly VERSION = "1.0.0";
  private readonly MAX_STORAGE_SIZE = 5 * 1024 * 1024; // 5MB
  private readonly CLEANUP_THRESHOLD = 7 * 24 * 60 * 60 * 1000; // 7 days
  private storageAvailable: boolean;

  private constructor() {
    this.storageAvailable = this.checkStorageAvailable();
    if (this.storageAvailable) {
      this.cleanupExpiredStates();
    }
  }

  public static getInstance(): QuizStorageService {
    if (!QuizStorageService.instance) {
      QuizStorageService.instance = new QuizStorageService();
    }
    return QuizStorageService.instance;
  }

  private checkStorageAvailable(): boolean {
    try {
      const test = "__quiz_storage_test__";
      localStorage.setItem(test, "test");
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      console.warn(
        "[QuizStorage] localStorage not available (incognito mode or disabled)"
      );
      return false;
    }
  }

  public hasStorageAvailable(): boolean {
    return this.storageAvailable;
  }

  private getStorageKey(quizId: string): string {
    const tabId = sessionStorage.getItem("quiz_tab_id") || this.generateTabId();
    return `${this.STORAGE_KEY_PREFIX}${quizId}_${tabId}`;
  }

  private generateTabId(): string {
    const tabId = `tab_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    sessionStorage.setItem("quiz_tab_id", tabId);
    return tabId;
  }

  // ============================================================
  // 🆕 NEW: Find ALL storage keys matching a quizId (any tab)
  // ============================================================
  private findAllKeysForQuiz(quizId: string): string[] {
    const matchingKeys: string[] = [];
    const prefix = `${this.STORAGE_KEY_PREFIX}${quizId}_`;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        matchingKeys.push(key);
      }
    }

    return matchingKeys;
  }

  // ============================================================
  // 🆕 NEW: Find the MOST RECENT state for a quizId (any tab)
  // ============================================================
  private findMostRecentStateKey(quizId: string): string | null {
    const keys = this.findAllKeysForQuiz(quizId);

    if (keys.length === 0) return null;
    if (keys.length === 1) return keys[0];

    // Find the most recently updated state
    let mostRecentKey = keys[0];
    let mostRecentTime = 0;

    for (const key of keys) {
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          const state: SerializableQuizState = JSON.parse(stored);
          if (state.lastUpdate > mostRecentTime) {
            mostRecentTime = state.lastUpdate;
            mostRecentKey = key;
          }
        }
      } catch (e) {
        // Skip corrupted entries
        continue;
      }
    }

    return mostRecentKey;
  }

  private createMetadata(): StorageMetadata {
    return {
      version: this.VERSION,
      timestamp: Date.now(),
      browser: navigator.userAgent,
      tabId: sessionStorage.getItem("quiz_tab_id") || undefined,
    };
  }

  private serializeAnswers(answers: Map<string, any>): Array<[string, any]> {
    const serialized: Array<[string, any]> = [];
    answers.forEach((value, key) => {
      let serializedValue = value;
      if (Array.isArray(value)) {
        serializedValue = [...value];
      } else if (value && typeof value === "object" && !Array.isArray(value)) {
        serializedValue = { ...value };
      }
      serialized.push([key, serializedValue]);
    });
    return serialized;
  }

  private deserializeAnswers(
    serialized: Array<[string, any]>
  ): Map<string, any> {
    const answers = new Map<string, any>();
    serialized.forEach(([key, value]) => {
      answers.set(key, value);
    });
    return answers;
  }

  private calculateStorageSize(): number {
    let totalSize = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.STORAGE_KEY_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value) {
          totalSize += key.length + value.length;
        }
      }
    }
    return totalSize;
  }

  private cleanupExpiredStates(): number {
    try {
      const now = Date.now();
      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.STORAGE_KEY_PREFIX)) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const state: SerializableQuizState = JSON.parse(stored);
              if (now - state.lastUpdate > this.CLEANUP_THRESHOLD) {
                keysToRemove.push(key);
              }
            }
          } catch (error) {
            keysToRemove.push(key);
          }
        }
      }

      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
        console.info(`[QuizStorage] Cleaned up expired state: ${key}`);
      });

      if (keysToRemove.length > 0) {
        const metadata = {
          lastCleanup: now,
          cleanedItems: keysToRemove.length,
        };
        localStorage.setItem(this.METADATA_KEY, JSON.stringify(metadata));
      }

      return keysToRemove.length;
    } catch (error) {
      console.error("[QuizStorage] Cleanup failed:", error);
      return 0;
    }
  }

  private makeSpace(requiredSize: number): boolean {
    try {
      const cleanedCount = this.cleanupExpiredStates();
      console.info(`[QuizStorage] Cleaned ${cleanedCount} expired states`);

      const currentSize = this.calculateStorageSize();
      if (currentSize + requiredSize <= this.MAX_STORAGE_SIZE) {
        return true;
      }

      const allStates: Array<{ key: string; timestamp: number }> = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.STORAGE_KEY_PREFIX)) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const state: SerializableQuizState = JSON.parse(stored);
              allStates.push({ key, timestamp: state.lastUpdate });
            }
          } catch (error) {
            if (key) localStorage.removeItem(key);
          }
        }
      }

      allStates.sort((a, b) => a.timestamp - b.timestamp);

      let removedSize = 0;
      for (const state of allStates) {
        const value = localStorage.getItem(state.key);
        if (value) {
          removedSize += state.key.length + value.length;
          localStorage.removeItem(state.key);
          console.info(
            `[QuizStorage] Removed old state to make space: ${state.key}`
          );

          if (removedSize >= requiredSize) {
            break;
          }
        }
      }

      return (
        this.calculateStorageSize() + requiredSize <= this.MAX_STORAGE_SIZE
      );
    } catch (error) {
      console.error("[QuizStorage] Failed to make space:", error);
      return false;
    }
  }

  public saveQuizState(quizState: QuizState): SaveResult {
    if (!this.storageAvailable) {
      return {
        success: false,
        error: "STORAGE_UNAVAILABLE",
        message: "localStorage is not available (private browsing or disabled)",
      };
    }

    try {
      const serializableState: SerializableQuizState = {
        ...quizState,
        userAnswers: this.serializeAnswers(quizState.userAnswers),
        lastUpdate: Date.now(),
        metadata: this.createMetadata(),
      };

      let serialized: string;
      try {
        serialized = JSON.stringify(serializableState);

        console.log("[QuizStorage] Serializing state:", {
          quizId: quizState.quizId,
          answerTypes: serializableState.userAnswers.map(([key, value]) => ({
            key,
            type: Array.isArray(value) ? "array" : typeof value,
            isObject: typeof value === "object" && !Array.isArray(value),
            value: value,
            stringified: JSON.stringify(value),
          })),
          fullStorageObject: serializableState,
        });
      } catch (error: any) {
        console.error("[QuizStorage] Serialization error:", error);
        return {
          success: false,
          error: "SERIALIZATION_ERROR",
          message: `Failed to serialize state: ${error.message || "Unknown"}`,
        };
      }

      const storageKey = this.getStorageKey(quizState.quizId);
      let cleanupPerformed = false;

      try {
        localStorage.setItem(storageKey, serialized);
      } catch (error: any) {
        if (
          error.name === "QuotaExceededError" ||
          error.message?.includes("quota")
        ) {
          console.warn(
            "[QuizStorage] Storage quota exceeded, attempting cleanup"
          );
          cleanupPerformed = true;

          if (this.makeSpace(serialized.length)) {
            try {
              localStorage.setItem(storageKey, serialized);
            } catch (retryError) {
              return {
                success: false,
                error: "QUOTA_EXCEEDED",
                message: "Storage quota exceeded even after cleanup",
                cleanupPerformed: true,
              };
            }
          } else {
            return {
              success: false,
              error: "QUOTA_EXCEEDED",
              message: "Unable to free enough space for quiz state",
              cleanupPerformed: true,
            };
          }
        } else {
          throw error;
        }
      }

      console.info(`[QuizStorage] Saved quiz state for ${quizState.quizId}`, {
        questionsAnswered: quizState.userAnswers.size,
        currentQuestion: quizState.currentQuestionIndex + 1,
        totalQuestions: quizState.totalQuestions,
        storageSize: serialized.length,
        cleanupPerformed,
      });

      return {
        success: true,
        cleanupPerformed,
      };
    } catch (error: any) {
      console.error("[QuizStorage] Unexpected error in saveQuizState:", error);
      return {
        success: false,
        error: "UNKNOWN_ERROR",
        message: `Unexpected error: ${error.message || "Unknown error"}`,
      };
    }
  }

  // ============================================================
  // 🔧 UPDATED: loadQuizState - Now finds state from ANY tab
  // ============================================================
  public loadQuizState(quizId: string): QuizState | null {
    console.log("📦 LOAD STATE: Start", {
      quizId,
      storageAvailable: this.storageAvailable,
    });

    if (!this.storageAvailable) {
      console.log("📦 LOAD STATE: Storage not available");
      return null;
    }

    try {
      // 🆕 First try current tab's key, then find most recent across all tabs
      let storageKey = this.getStorageKey(quizId);
      let stored = localStorage.getItem(storageKey);

      // If not found with current tab's key, search all tabs
      if (!stored) {
        console.log(
          "📦 LOAD STATE: Not found with current tab key, searching all tabs..."
        );
        const mostRecentKey = this.findMostRecentStateKey(quizId);
        if (mostRecentKey) {
          storageKey = mostRecentKey;
          stored = localStorage.getItem(storageKey);
          console.log("📦 LOAD STATE: Found state from different tab", {
            mostRecentKey,
          });
        }
      }

      console.log("📦 LOAD STATE: Storage Key", { storageKey });
      console.log("📦 LOAD STATE: Raw Stored Data", {
        exists: !!stored,
        length: stored?.length,
        preview: stored?.substring(0, 100),
      });

      if (!stored) {
        console.log("📦 LOAD STATE: No stored data found");
        return null;
      }

      const serializableState: SerializableQuizState = JSON.parse(stored);
      console.log("📦 LOAD STATE: Parsed State", {
        moduleName: serializableState.moduleName,
        week: serializableState.week,
        answersArray: serializableState.userAnswers,
        answersCount: serializableState.userAnswers.length,
      });

      if (!this.validateStoredState(serializableState)) {
        console.warn(
          "[QuizStorage] Invalid state structure, removing corrupted data"
        );
        localStorage.removeItem(storageKey);
        return null;
      }

      const now = Date.now();
      if (now - serializableState.lastUpdate > this.CLEANUP_THRESHOLD) {
        console.info("[QuizStorage] State expired, removing");
        localStorage.removeItem(storageKey);
        return null;
      }

      const userAnswers = this.deserializeAnswers(
        serializableState.userAnswers
      );

      console.log("[QuizStorage] Deserializing state:", {
        quizId,
        answerTypes: Array.from(userAnswers.entries()).map(([key, value]) => ({
          key,
          type: Array.isArray(value) ? "array" : typeof value,
          value: Array.isArray(value)
            ? `[${value.length} items]`
            : typeof value === "object"
            ? JSON.stringify(value)
            : value,
        })),
      });

      const quizState: QuizState = {
        moduleName: serializableState.moduleName,
        week: serializableState.week,
        currentQuestionIndex: serializableState.currentQuestionIndex,
        userAnswers,
        startTime: serializableState.startTime,
        lastUpdate: serializableState.lastUpdate,
        quizId: serializableState.quizId,
        totalQuestions: serializableState.totalQuestions,
        progressStats: serializableState.progressStats,
      };

      console.info(`[QuizStorage] Loaded quiz state for ${quizId}`, {
        questionsAnswered: userAnswers.size,
        currentQuestion: quizState.currentQuestionIndex + 1,
        age:
          Math.round((now - serializableState.lastUpdate) / 1000 / 60) +
          " minutes",
      });

      return quizState;
    } catch (error) {
      console.error("[QuizStorage] Failed to load quiz state:", error);
      try {
        localStorage.removeItem(this.getStorageKey(quizId));
      } catch (e) {
        // Ignore removal errors
      }
      return null;
    }
  }

  private validateStoredState(state: any): state is SerializableQuizState {
    return (
      state &&
      typeof state.moduleName === "string" &&
      typeof state.week === "number" &&
      typeof state.currentQuestionIndex === "number" &&
      Array.isArray(state.userAnswers) &&
      typeof state.startTime === "number" &&
      typeof state.lastUpdate === "number" &&
      typeof state.quizId === "string" &&
      typeof state.totalQuestions === "number" &&
      state.progressStats &&
      typeof state.progressStats.completed === "number" &&
      state.metadata &&
      typeof state.metadata.timestamp === "number"
    );
  }

  // ============================================================
  // 🔧 UPDATED: removeQuizState - Now removes ALL tabs' states
  // ============================================================
  public removeQuizState(quizId: string): boolean {
    if (!this.storageAvailable) {
      return false;
    }

    try {
      // 🆕 Find and remove ALL states matching this quizId (any tab)
      const keysToRemove = this.findAllKeysForQuiz(quizId);

      if (keysToRemove.length === 0) {
        console.info(`[QuizStorage] No states found for ${quizId}`);
        return false;
      }

      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
        console.info(`[QuizStorage] Removed quiz state: ${key}`);
      });

      console.info(
        `[QuizStorage] Removed ${keysToRemove.length} state(s) for ${quizId}`
      );
      return true;
    } catch (error) {
      console.error("[QuizStorage] Failed to remove quiz state:", error);
      return false;
    }
  }

  // ============================================================
  // 🔧 UPDATED: hasStoredState - Now checks ALL tabs
  // ============================================================
  public hasStoredState(quizId: string): boolean {
    if (!this.storageAvailable) {
      return false;
    }

    try {
      // 🆕 Check if ANY state exists for this quizId (any tab)
      const keys = this.findAllKeysForQuiz(quizId);
      return keys.length > 0;
    } catch (error) {
      return false;
    }
  }

  public getAllStoredQuizzes(): Array<{
    quizId: string;
    metadata: StorageMetadata;
    progress: number;
  }> {
    const quizzes: Array<{
      quizId: string;
      metadata: StorageMetadata;
      progress: number;
    }> = [];

    if (!this.storageAvailable) {
      return quizzes;
    }

    try {
      // Track unique quizIds to avoid duplicates from multiple tabs
      const seenQuizIds = new Set<string>();

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.STORAGE_KEY_PREFIX)) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const state: SerializableQuizState = JSON.parse(stored);

              // Skip if we've already seen this quizId
              if (seenQuizIds.has(state.quizId)) {
                continue;
              }
              seenQuizIds.add(state.quizId);

              const progress =
                state.totalQuestions > 0
                  ? (state.userAnswers.length / state.totalQuestions) * 100
                  : 0;

              quizzes.push({
                quizId: state.quizId,
                metadata: state.metadata,
                progress: Math.round(progress),
              });
            }
          } catch (error) {
            continue;
          }
        }
      }
    } catch (error) {
      console.error("[QuizStorage] Failed to get stored quizzes:", error);
    }

    return quizzes.sort((a, b) => b.metadata.timestamp - a.metadata.timestamp);
  }

  public getStorageInfo(): {
    totalStates: number;
    currentSize: number;
    maxSize: number;
    utilizationPercentage: number;
    storageAvailable: boolean;
  } {
    const baseInfo = {
      totalStates: 0,
      currentSize: 0,
      maxSize: this.MAX_STORAGE_SIZE,
      utilizationPercentage: 0,
      storageAvailable: this.storageAvailable,
    };

    if (!this.storageAvailable) {
      return baseInfo;
    }

    try {
      const totalStates = this.getAllStoredQuizzes().length;
      const currentSize = this.calculateStorageSize();
      const utilizationPercentage = (currentSize / this.MAX_STORAGE_SIZE) * 100;

      return {
        totalStates,
        currentSize,
        maxSize: this.MAX_STORAGE_SIZE,
        utilizationPercentage: Math.round(utilizationPercentage * 100) / 100,
        storageAvailable: this.storageAvailable,
      };
    } catch (error) {
      console.error("[QuizStorage] Failed to get storage info:", error);
      return baseInfo;
    }
  }

  public clearAllStates(): boolean {
    if (!this.storageAvailable) {
      return false;
    }

    try {
      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.STORAGE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => localStorage.removeItem(key));
      localStorage.removeItem(this.METADATA_KEY);

      console.info(`[QuizStorage] Cleared ${keysToRemove.length} quiz states`);
      return true;
    } catch (error) {
      console.error("[QuizStorage] Failed to clear all states:", error);
      return false;
    }
  }

  public debugStoredState(quizId: string): void {
    if (!this.storageAvailable) {
      console.log("[QuizStorage Debug] Storage not available");
      return;
    }

    try {
      // 🆕 Show ALL states for this quizId
      const keys = this.findAllKeysForQuiz(quizId);
      console.log(
        `[QuizStorage Debug] Found ${keys.length} state(s) for ${quizId}:`
      );

      keys.forEach((key, index) => {
        const stored = localStorage.getItem(key);
        if (stored) {
          const parsed = JSON.parse(stored);
          console.log(`[QuizStorage Debug] State ${index + 1}:`, {
            key,
            tabId: parsed.metadata?.tabId,
            lastUpdate: new Date(parsed.lastUpdate).toLocaleString(),
            answersCount: parsed.userAnswers?.length || 0,
          });
        }
      });

      if (keys.length === 0) {
        console.log("[QuizStorage Debug] No states found for", quizId);
      }
    } catch (error) {
      console.error("[QuizStorage Debug] Error:", error);
    }
  }
}

// Export singleton instance
export const quizStorageService = QuizStorageService.getInstance();
